// Utility functions for device fingerprinting and token management
const DeviceUtils = {
  // Generate more comprehensive device fingerprint
  generateFingerprint: () => {
    try {
      // Get canvas fingerprint
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
      const canvasFingerprint = canvas.toDataURL();
      
      // Get essential device information only
      const deviceInfo = {
        // Browser and platform info
        ua: navigator.userAgent.substring(0, 50), // Truncate user agent
        p: navigator.platform,
        l: navigator.language,
        
        // Screen information
        sw: window.screen.width,
        sh: window.screen.height,
        cd: window.screen.colorDepth,
        
        // Window information
        ww: window.innerWidth,
        wh: window.innerHeight,
        
        // Timezone
        tz: new Date().getTimezoneOffset(),
        
        // Hardware information
        hc: navigator.hardwareConcurrency || 0,
        
        // Canvas fingerprint (shortened)
        cf: canvasFingerprint.substring(0, 100)
      };
      
      // Create a compact fingerprint string
      const fingerprintString = JSON.stringify(deviceInfo);
      
      // Create a short hash
      let hash = 0;
      for (let i = 0; i < fingerprintString.length; i++) {
        const char = fingerprintString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      
      // Convert to base36 for shorter representation
      const shortFingerprint = Math.abs(hash).toString(36);
      
      console.log('Generated short device fingerprint:', {
        hash: Math.abs(hash),
        fingerprint: shortFingerprint,
        length: shortFingerprint.length
      });
      
      return shortFingerprint;
    } catch (error) {
      console.error('Error generating device fingerprint:', error);
      // Fallback to basic fingerprint
      const fallback = navigator.userAgent.substring(0, 20) + navigator.platform + window.screen.width;
      return btoa(fallback).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
    }
  },

  // Generate secure token with minimal length
  generateToken: (phoneNumber, deviceId) => {
    try {
      const timestamp = Date.now();
      const sessionId = Math.random().toString(36).substring(2, 8); // Shorter session ID
      
      // Create compact payload
      const payload = {
        p: phoneNumber, // phone
        d: deviceId,    // device
        t: timestamp,   // timestamp
        s: sessionId    // session
      };
      
      const tokenString = JSON.stringify(payload);
      const encodedToken = btoa(tokenString).replace(/[^a-zA-Z0-9]/g, '');
      
      console.log('Generated short token:', {
        phoneNumber,
        deviceId: deviceId.substring(0, 8) + '...',
        tokenLength: encodedToken.length,
        sessionId
      });
      
      return encodedToken;
    } catch (error) {
      console.error('Error generating token:', error);
      throw new Error('Không thể tạo token bảo mật');
    }
  },

  // Verify token with enhanced validation
  verifyToken: (token) => {
    try {
      if (!token || typeof token !== 'string') {
        console.log('Invalid token format');
        return { isValid: false, phoneNumber: null, deviceId: null, reason: 'invalid_format' };
      }
      
      // Decode token
      const decodedString = atob(token.replace(/[^a-zA-Z0-9]/g, ''));
      const payload = JSON.parse(decodedString);
      
      // Validate payload structure (using short field names)
      if (!payload.p || !payload.d || !payload.t || !payload.s) {
        console.log('Token payload missing required fields');
        return { isValid: false, phoneNumber: null, deviceId: null, reason: 'invalid_payload' };
      }
      
      // Get current device fingerprint
      const currentDeviceId = DeviceUtils.generateFingerprint();
      
      // Check if device matches
      if (payload.d !== currentDeviceId) {
        console.log('Device mismatch:', {
          tokenDeviceId: payload.d.substring(0, 8) + '...',
          currentDeviceId: currentDeviceId.substring(0, 8) + '...'
        });
        return { 
          isValid: false, 
          phoneNumber: null, 
          deviceId: payload.d, 
          reason: 'device_mismatch',
          currentDeviceId: currentDeviceId
        };
      }
      
      // Check token age (24 hours)
      const tokenAge = Date.now() - payload.t;
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      
      if (tokenAge > maxAge) {
        console.log('Token expired:', {
          tokenAge: Math.round(tokenAge / (1000 * 60 * 60)) + ' hours',
          maxAge: Math.round(maxAge / (1000 * 60 * 60)) + ' hours'
        });
        return { 
          isValid: false, 
          phoneNumber: payload.p, 
          deviceId: payload.d, 
          reason: 'expired',
          tokenAge,
          maxAge
        };
      }
      
      console.log('Token verification successful:', {
        phoneNumber: payload.p,
        deviceId: payload.d.substring(0, 8) + '...',
        sessionId: payload.s,
        age: Math.round(tokenAge / (1000 * 60)) + ' minutes'
      });
      
      return {
        isValid: true,
        phoneNumber: payload.p,
        deviceId: payload.d,
        sessionId: payload.s,
        timestamp: payload.t,
        age: tokenAge
      };
    } catch (error) {
      console.error('Token verification error:', error);
      return { 
        isValid: false, 
        phoneNumber: null, 
        deviceId: null, 
        reason: 'verification_error',
        error: error.message
      };
    }
  },

  // Get device information for debugging
  getDeviceInfo: () => {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth
      },
      window: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      fingerprint: DeviceUtils.generateFingerprint()
    };
  }
};

export default DeviceUtils;
